import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIngresos, deleteIngreso } from '../actions/ingresoActions';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import IngresoForm from './IngresoForm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Pagination, Table, Button } from 'react-bootstrap';
import CustomPrintModal from './CustomPrintModal';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

const IngresosList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { ingresos, error } = useSelector(state => state.ingreso);
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [content, setContent] = useState(null);

    useEffect(() => {
        dispatch(fetchIngresos());
    }, [dispatch]);

    const handleDelete = (nrorec) => {
        confirmAlert({
            title: 'Confirmar eliminación',
            message: '¿Estás seguro de eliminar este ingreso y sus detalles?',
            buttons: [
                {
                    label: 'Sí',
                    onClick: () => {
                        dispatch(deleteIngreso(nrorec))
                            .then(() => {
                                toast.success('Ingreso eliminado');
                                dispatch(fetchIngresos());
                            })
                            .catch(error => toast.error(`Error: ${error.message}`));
                    }
                },
                { label: 'No' }
            ]
        });
    };

    const handlePrintModalShow = () => {
        const content = {
            head: [["N°", "Recibo", "Beneficiario", "Concepto", "Fecha", "Monto Total"]],
            body: ingresos.map((ingreso, index) => [
                index + 1,
                ingreso.nrorec,
                ingreso.beneficiario,
                ingreso.concepto,
                moment(ingreso.fecha).format('DD/MM/YYYY'),
                `$${Number(ingreso.monto).toFixed(2)}`
            ])
        };
        setContent(content);
        setShowPrintModal(true);
    };

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentIngresos = ingresos.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(ingresos.length / itemsPerPage);

    if (error) toast.error(`Error: ${error}`);

    return (
        <div className="list2-container container-list">
            <h2>Listado de Ingresos</h2>
            
            <div className="row mt-3">
                <div className="col-lg-8">
                    <label>Registros por página:</label>
                    <select value={itemsPerPage} disabled>
                        <option value="10">10</option>
                    </select>
                </div>
                <div className="col-lg-3 float-lg-end">
                    <Button variant="accent" onClick={() => {
                        setIsEdit(false);
                        setSelectedId(null);
                        setShowForm(true);
                    }}>
                        <i className="bi bi-plus-lg"></i> Nuevo Ingreso
                    </Button>
                </div>
            </div>

            <div className="table-container">
                <Table hover striped>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Recibo</th>
                            <th>Beneficiario</th>
                            <th>Concepto</th>
                            <th>Fecha</th>
                            <th>Monto Total</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentIngresos.map((ingreso, index) => (
                            <tr key={ingreso.nrorec}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{ingreso.nrorec}</td>
                                <td>{ingreso.beneficiario}</td>
                                <td>{ingreso.concepto}</td>
                                <td>{moment(ingreso.fecha).format('DD/MM/YYYY')}</td>
                                <td>${Number(ingreso.monto).toFixed(2)}</td>
                                <td>
                                    <Button variant="outline-accent" size="sm" onClick={() => {
                                        setSelectedId(ingreso.nrorec);
                                        setIsEdit(true);
                                        setShowForm(true);
                                    }}>
                                        <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(ingreso.nrorec)}>
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="button-group separate">
                <Button variant="secondary" onClick={() => navigate('/')}>
                    <i className="bi bi-arrow-left"></i> Volver
                </Button>
                <Button variant="accent" onClick={handlePrintModalShow}>
                    <i className="bi bi-printer"></i> Imprimir
                </Button>
            </div>

            <Pagination className="mt-3">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, idx) => (
                    <Pagination.Item key={idx + 1} active={idx + 1 === currentPage} onClick={() => setCurrentPage(idx + 1)}>
                        {idx + 1}
                    </Pagination.Item>
                ))}
                <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>

            {showForm && (
                <IngresoForm 
                    show={showForm} 
                    handleClose={() => {
                        setShowForm(false);
                        dispatch(fetchIngresos()); // Refresh al cerrar
                    }} 
                    isEdit={isEdit} 
                    ingresoId={selectedId} 
                />
            )}

            <CustomPrintModal 
                show={showPrintModal} 
                handleClose={() => setShowPrintModal(false)} 
                title="LISTADO DE INGRESOS" 
                content={content} 
            />
        </div>
    );
};

export default IngresosList;